import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { cn } from '@/lib/utils';
import {
  DealDisplayStatus,
  dealDisplayStatusAtom,
} from '@/states/deal-display-status.state';
import Icon from '@appchoose/icon';
import { useAtomValue } from 'jotai';
import {
  LockKeyhole,
  Sparkles,
  SquareDashedBottomCode,
  SquareM,
} from 'lucide-react';

// Wrapper component for @appchoose/icon to match Lucide icon API
export const ChooseIcon: React.FC<React.SVGProps<SVGSVGElement>> = ({
  className,
  ...props
}) => {
  return React.createElement(Icon, {
    icon: 'choose',
    size: 'medium',
    className,
    ...props,
  });
};

export const statusConfigs = {
  empty: {
    key: 'empty',
    translationKey: 'components.offer_header.status.empty',
    icon: Sparkles,
    backgroundColorClass: 'bg-gray-100',
    textColorClass: 'text-gray-600',
  },
  importing: {
    key: 'importing',
    translationKey: 'components.offer_header.status.importing',
    icon: SquareDashedBottomCode,
    backgroundColorClass: 'bg-gray-100',
    textColorClass: 'text-gray-600',
  },
  choose_editing: {
    key: 'choose_editing',
    translationKey: 'components.offer_header.status.choose_editing',
    icon: ChooseIcon,
    backgroundColorClass: 'bg-blue-50',
    textColorClass: 'text-blue-900',
  },
  brand_editing: {
    key: 'brand_editing',
    translationKey: 'components.offer_header.status.brand_editing',
    icon: SquareM,
    backgroundColorClass: 'bg-purple-50',
    textColorClass: 'text-purple-600',
  },
  closed: {
    key: 'closed',
    translationKey: 'components.offer_header.status.closed',
    icon: LockKeyhole,
    backgroundColorClass: 'bg-slate-100',
    textColorClass: 'text-slate-600',
  },
} as const satisfies Record<DealDisplayStatus, unknown>;

const LABEL_VISIBLE_MS = 2000;

function StatusLabel({ iconOnly = false }: { iconOnly?: boolean }) {
  const { t, i18n } = useTranslation();
  const displayStatus = useAtomValue(dealDisplayStatusAtom);
  const [compact, setCompact] = useState(false);
  const slidingCompact = compact && iconOnly;

  useEffect(() => {
    if (!displayStatus) return;
    setCompact(false);
    const id = window.setTimeout(() => setCompact(true), LABEL_VISIBLE_MS);
    return () => window.clearTimeout(id);
  }, [displayStatus, i18n.language]);

  if (!displayStatus) {
    return null;
  }

  const statusConfig = statusConfigs[displayStatus];
  const Icon = statusConfig.icon;

  return (
    <span
      className={cn(
        'group inline-flex h-9 cursor-default items-center  rounded-sm px-2 text-xs font-medium ',
        statusConfig.backgroundColorClass,
        statusConfig.textColorClass
      )}
    >
      <Icon
        className={cn('size-5 shrink-0 px-0.5', statusConfig.textColorClass)}
      />
      <span
        className={cn(
          'grid overflow-hidden transition-[grid-template-columns] duration-300 ease-in-out motion-reduce:transition-none',
          slidingCompact
            ? 'grid-cols-[0fr] group-hover:grid-cols-[1fr]'
            : 'grid-cols-[1fr]'
        )}
      >
        <span className="min-w-0 overflow-hidden whitespace-nowrap">
          &nbsp;{t(statusConfig.translationKey)}
        </span>
      </span>
    </span>
  );
}

export default StatusLabel;
